import {useMutation} from '@tanstack/react-query';
import {apiClient, queryClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {toast} from '@ui/toast/toast';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';

interface ActivateLicensePayload {
  id: number;
}

interface ActivateLicenseResponse extends BackendResponse {
  user?: any;
}

export function useActivateLicense() {
  return useMutation({
    mutationFn: (payload: ActivateLicensePayload) => activateLicense(payload),
    onSuccess: response => {
      if (response.user) {
        // Update user data if needed
        localStorage.setItem('userData', JSON.stringify(response.user));
      }
      queryClient.invalidateQueries({queryKey: ['licenses']});
    },
    onError: err => showHttpErrorToast(err),
  });
}

function activateLicense(
  payload: ActivateLicensePayload,
): Promise<ActivateLicenseResponse> {
  // Generate token for license activation
  return apiClient.post('drive/generate-token', {
    endpoint_type: 'products',
  }).then(tokenResponse => {
    const token = tokenResponse.data.token;
    
    // Call external APCROSA API directly
    return fetch('https://apcrosa.syscomlive.co.uk/api/v1/drive/license/activate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Token': token,
      },
      body: JSON.stringify({
        id: payload.id,
        for_drive: true,
      }),
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to activate license');
      }
      return response.json();
    }).then(data => {
      if (data.status !== 1) {
        throw new Error(data.msg || 'Failed to activate license');
      }
      return data;
    });
  });
}
